import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ParseKeys } from 'i18next'
import { publicAPI, privateAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { Building2, Car, Users as UsersIcon, ArrowUp, ArrowDown, ChevronsUpDown, Loader2, Calendar } from 'lucide-react'
import { ColumnDefinition } from '../utils/exportToCsv'
import ChangeIndicatorShared, { ChangeCount as ChangeCountShared } from '../components/ChangeIndicator'
// import PeriodInfoBanner from '../components/PeriodInfoBanner'
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { formatLeasingselskap } from '../utils/formatLeasingselskap'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMineMerker } from '../hooks/useMineMerker'
import { useMirrorSharedDates } from '../hooks/useMirrorSharedDates'
import { localizeApiDetail } from '../i18n/apiError'

type OrgKey = ParseKeys<'organizations'>

// Helper function to safely parse numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

// ---------- Næring (tab 1) types ----------

interface NaeringRow {
  rank: number
  org_number: string
  org_name: string
  short_name: string
  classification: string
  current_count: number
  market_share_percentage: number
  previous_count: number
  change_count: number
  change_percent: number | null
}

interface OrganizationClassification {
  classification: string
  count: number
  percentage: number
}

// ---------- Company-car tab types (unchanged) ----------

interface CompanyCarBrandData {
  rank?: number
  brand: string
  total_company_cars: number
  owned_count: number
  owned_percentage: number
  leased_count: number
  leased_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  ev_count: number
  ev_percentage: number
}

interface CompanyCarModelData {
  rank?: number
  brand: string
  master_model: string
  total_company_cars: number
  owned_count: number
  owned_percentage: number
  leased_count: number
  leased_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  ev_count: number
  ev_percentage: number
}

// Totals over every company car in the selection, not only the top-N rows (#957)
type CompanyCarSummary = Omit<CompanyCarBrandData, 'rank' | 'brand'>

// ---------- Brand Org tabs (Merkekjøp / Merkeleasing / kombinert) types ----------

type BrandOrgTab = 'brand_buyers' | 'brand_leasers' | 'brand_combined'

const BRAND_ORG_TABS: BrandOrgTab[] = ['brand_buyers', 'brand_leasers', 'brand_combined']

const BRAND_ORG_TAB_LABELS: Record<BrandOrgTab, OrgKey> = {
  brand_buyers: 'analytics.tabs.brandBuyers',
  brand_leasers: 'analytics.tabs.brandLeasers',
  brand_combined: 'analytics.tabs.brandCombined',
}

interface BrandOrgTopModel {
  model_name: string
  count: number
}

interface BrandOrgRow {
  rank: number
  org_number: string
  org_name: string
  classification: string
  vehicle_count: number
  owned_count?: number
  leased_count?: number
  brand_market_share_pct: number
  org_brand_share_pct: number
  top_3_models: BrandOrgTopModel[]
  yoy_change: number
  yoy_change_pct: number | null
  brand: string | null
}

// Classification labels (translation keys, resolved at render)
const classificationLabels: Record<string, OrgKey> = {
  'Leasingselskap': 'analytics.classification.leasingselskap',
  'bilbransje': 'analytics.classification.bilbransje',
  'bilutleieselskap': 'analytics.classification.bilutleieselskap',
  'kommune': 'analytics.classification.kommune',
  'other_organization': 'analytics.classification.otherOrganization',
  'private': 'analytics.classification.private'
}

// Short codes for classifications (fallback when short_name is empty)
const classificationShortCodes: Record<string, string> = {
  'Leasingselskap': 'LEA',
  'bilbransje': 'BIL',
  'bilutleieselskap': 'BUL',
  'kommune': 'KOM',
  'other_organization': 'ANO',
  'private': 'PRI'
}

// ---------- Output Mode (time period selector) ----------

type OutputMode = 'year_to_date' | 'month_to_date' | 'last_month' | 'last_week' | 'last_year' | 'custom'

const OUTPUT_MODE_LABELS: Record<OutputMode, OrgKey> = {
  year_to_date: 'analytics.outputMode.yearToDate',
  month_to_date: 'analytics.outputMode.monthToDate',
  last_month: 'analytics.outputMode.lastMonth',
  last_week: 'analytics.outputMode.lastWeek',
  last_year: 'analytics.outputMode.lastYear',
  custom: 'analytics.outputMode.custom',
}

// ---------- Næring filter ----------

type NaeringFilter = 'alle' | 'leasing' | 'eier_og_leasingtaker'

const NAERING_FILTER_LABELS: Record<NaeringFilter, OrgKey> = {
  alle: 'analytics.naeringFilter.alle',
  leasing: 'analytics.naeringFilter.leasing',
  eier_og_leasingtaker: 'analytics.naeringFilter.eierOgLeasingtaker',
}

const NAERING_HEADER_LABELS: Record<NaeringFilter, OrgKey> = {
  alle: 'analytics.headerLabels.alle',
  leasing: 'analytics.headerLabels.leasing',
  eier_og_leasingtaker: 'analytics.headerLabels.eierOgLeasingtaker',
}

// ---------- Date helpers ----------

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getDateRangeForMode(mode: OutputMode, maxDataDate?: string | null): { startDate: string; endDate: string } {
  const now = new Date()
  let start: Date
  let end: Date = (maxDataDate && (mode === 'year_to_date' || mode === 'month_to_date'))
    ? new Date(maxDataDate + 'T12:00:00')
    : new Date(now)

  switch (mode) {
    case 'year_to_date':
      start = new Date(now.getFullYear(), 0, 1)
      break
    case 'month_to_date':
      start = new Date(now.getFullYear(), now.getMonth(), 1)
      break
    case 'last_month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      end = new Date(now.getFullYear(), now.getMonth(), 0)
      break
    case 'last_week': {
      const today = new Date()
      const dayOfWeek = today.getDay() // 0=Sun, 1=Mon, ...
      // Find Monday of the current week
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const thisMonday = new Date(today)
      thisMonday.setDate(today.getDate() - daysToMonday)
      // "Forrige uke" = the full week before the current week (Mon–Sun)
      const lastMonday = new Date(thisMonday)
      lastMonday.setDate(thisMonday.getDate() - 7)
      const lastSunday = new Date(lastMonday)
      lastSunday.setDate(lastMonday.getDate() + 6)
      return {
        startDate: toLocalDateStr(lastMonday),
        endDate: toLocalDateStr(lastSunday),
      }
    }
    case 'last_year':
      start = new Date(now.getFullYear() - 1, 0, 1)
      end = new Date(now.getFullYear() - 1, 11, 31)
      break
    default:
      start = new Date(now.getFullYear(), 0, 1)
      break
  }

  return {
    startDate: toLocalDateStr(start),
    endDate: toLocalDateStr(end),
  }
}

function shiftYearBack(dateStr: string): string {
  const parts = dateStr.split('-')
  return `${parseInt(parts[0]) - 1}-${parts[1]}-${parts[2]}`
}

function getPeriodColumnHeaders(mode: OutputMode): { current: OrgKey; previous: OrgKey } {
  switch (mode) {
    case 'year_to_date':
      return { current: 'analytics.periodHeaders.yearToDate.current', previous: 'analytics.periodHeaders.yearToDate.previous' }
    case 'month_to_date':
      return { current: 'analytics.periodHeaders.monthToDate.current', previous: 'analytics.periodHeaders.monthToDate.previous' }
    case 'last_month':
      return { current: 'analytics.periodHeaders.lastMonth.current', previous: 'analytics.periodHeaders.lastMonth.previous' }
    case 'last_week':
      return { current: 'analytics.periodHeaders.lastWeek.current', previous: 'analytics.periodHeaders.lastWeek.previous' }
    case 'last_year':
      return { current: 'analytics.periodHeaders.lastYear.current', previous: 'analytics.periodHeaders.lastYear.previous' }
    case 'custom':
      return { current: 'analytics.periodHeaders.custom.current', previous: 'analytics.periodHeaders.custom.previous' }
  }
}

// ---------- Change indicator components ----------
// Using shared components from ../components/ChangeIndicator
const ChangeIndicator = ChangeIndicatorShared
const ChangeCount = ChangeCountShared

// ---------- Firmakjøp merker / modeller TOTAL row ----------

const COMPANY_CAR_SHARES = ['owned', 'leased', 'private_leasing', 'company_leasing'] as const

function CompanyCarTotalRow({ summary, label, blankCells = 0 }: { summary: CompanyCarSummary; label: string; blankCells?: number }) {
  return (
    <tr className="bg-gray-50 dark:bg-gray-800/40 font-bold border-t border-b border-gray-300 dark:border-gray-600">
      <td className="text-center py-2">
        <span className="text-gray-900 dark:text-gray-100 font-bold">∑</span>
      </td>
      <td className="font-bold text-gray-900 dark:text-gray-100 py-2">{label}</td>
      {Array.from({ length: blankCells }, (_, i) => <td key={i} className="py-2"></td>)}
      <td className="text-right font-bold py-2">{toNumber(summary.total_company_cars).toLocaleString('nb-NO')}</td>
      {COMPANY_CAR_SHARES.map(name => (
        <td key={name} className="text-right font-bold py-2">
          {toNumber(summary[`${name}_percentage` as const]).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
          <span className="text-xs text-gray-500 ml-1">
            ({toNumber(summary[`${name}_count` as const]).toLocaleString('nb-NO')})
          </span>
        </td>
      ))}
    </tr>
  )
}

// ==========================================================
// Component
// ==========================================================

export default function OrganizationAnalytics() {
  const { t } = useTranslation('organizations')
  const classificationLabel = (c: string) => (classificationLabels[c] ? t(classificationLabels[c]) : c)
  // -------- Næring (tab 1) state --------
  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [naeringRows, setNaeringRows] = useState<NaeringRow[]>([])
  const [classificationData, setClassificationData] = useState<OrganizationClassification[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [naeringPage, setNaeringPage] = useState(1)
  const [naeringTotalPages, setNaeringTotalPages] = useState(1)
  const [naeringTotalItems, setNaeringTotalItems] = useState(0)
  const [naeringTotalVehicles, setNaeringTotalVehicles] = useState(0)
  const [prevYearMap, setPrevYearMap] = useState<Map<string, number>>(new Map())
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [naeringFilter, setNaeringFilter] = useState<NaeringFilter>('alle')
  const [classificationFilter, setClassificationFilter] = useState<string | null>(null)
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')

  // Tab state
  const [activeTab, setActiveTab] = useState<'organizations' | 'brands' | 'models' | BrandOrgTab>('organizations')

  // Company car data states (unchanged)
  const [brandData, setBrandData] = useState<CompanyCarBrandData[]>([])
  const [modelData, setModelData] = useState<CompanyCarModelData[]>([])
  const [brandSummary, setBrandSummary] = useState<CompanyCarSummary | null>(null)
  const [modelSummary, setModelSummary] = useState<CompanyCarSummary | null>(null)
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(false)

  // Brand org tabs state (Merkekjøp / Merkeleasing)
  const [brandOrgData, setBrandOrgData] = useState<BrandOrgRow[]>([])
  const [brandOrgLoading, setBrandOrgLoading] = useState(false)
  const [selectedBrands, setSelectedBrands] = useState<string[]>([])
  const [brandOrgPage, setBrandOrgPage] = useState(1)
  const [brandOrgTotalPages, setBrandOrgTotalPages] = useState(0)
  const [brandOrgTotalItems, setBrandOrgTotalItems] = useState(0)
  const [brandOrgSummary, setBrandOrgSummary] = useState<{
    total_organizations: number
    total_vehicles: number
    total_previous_count: number
    total_yoy_change: number
    total_yoy_change_pct: number | null
    top_3_models: BrandOrgTopModel[]
    total_owned?: number
    total_leased?: number
  } | null>(null)
  const [availableBrands, setAvailableBrands] = useState<string[]>([])
  const [brandOrgClassification, setBrandOrgClassification] = useState<string>('')

  // Date range state (maxDataDate)
  const [maxDataDate, setMaxDataDate] = useState<string | null>(null)
  const maxDataDateHook = useMaxDataDate()

  // Sync hook result into local state (for backward compat with existing getDateRangeForMode usage)
  useEffect(() => {
    if (maxDataDateHook) setMaxDataDate(maxDataDateHook)
  }, [maxDataDateHook])

  // Share the chosen period with the useYearDates pages (Leasing, Lån, …)
  useMirrorSharedDates(outputMode, customStartDate, customEndDate, maxDataDate)

  // Fetch brand list for the brand picker (Merkekjøp/Merkeleasing tabs)
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const range = getDateRangeForMode('year_to_date', maxDataDate)
        const res = await privateAPI.getCompanyCarsByBrand({ start_date: range.startDate, end_date: range.endDate, limit: 100 })
        const brands = ((res.data.data || []) as any[]).map((b: any) => b.brand).filter(Boolean).sort()
        setAvailableBrands(brands)
      } catch { /* silent */ }
    }
    if (maxDataDate) fetchBrands()
  }, [maxDataDate])

  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  const { activeMerker } = useMineMerker()

  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('organization-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  // Fetch sequence counter to prevent stale responses from overwriting newer data
  const naeringFetchId = useRef(0)

  // Sort states
  const [orgSortConfig, setOrgSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [brandSortConfig, setBrandSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [modelSortConfig, setModelSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // -------- Active date range for Næring tab --------
  const activeDateRange = useMemo(() => {
    if (outputMode === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate }
    }
    return getDateRangeForMode(outputMode, maxDataDate)
  }, [outputMode, customStartDate, customEndDate, maxDataDate])

  // Queried-state tracking — only update when API fetch actually runs
  const [queriedOutputMode, setQueriedOutputMode] = useState<OutputMode>(outputMode)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState(minePostnummer)
  const [queriedStartDate, setQueriedStartDate] = useState(activeDateRange.startDate)
  const [queriedEndDate, setQueriedEndDate] = useState(activeDateRange.endDate)
  const [queriedNaeringFilter, setQueriedNaeringFilter] = useState<NaeringFilter>(naeringFilter)
  const [queriedClassificationFilter, setQueriedClassificationFilter] = useState<string | null>(classificationFilter)

  // -------- Trigger search explicitly (Søk button or Enter key) --------

  const triggerSearch = () => { setDebouncedSearch(searchTerm); setNaeringPage(1) }
  const clearSearch = () => { setSearchTerm(''); setDebouncedSearch(''); setNaeringPage(1) }

  const goToNaeringPage = (page: number) => {
    if (page < 1 || page > naeringTotalPages) return
    setOrgSortConfig(null)
    fetchNaeringData(page)
  }

  // -------- Effects --------

  useEffect(() => {
    if (outputMode === 'custom' && (!customStartDate || !customEndDate)) return
    if (activeTab === 'organizations') {
      setNaeringPage(1)
      fetchNaeringData(1)
    } else if (activeTab === 'brands') {
      fetchBrandData()
    } else if (activeTab === 'models') {
      fetchModelData()
    } else if (BRAND_ORG_TABS.includes(activeTab as BrandOrgTab) && selectedBrands.length > 0) {
      fetchBrandOrgData(activeTab as BrandOrgTab, 1)
    }
  }, [activeTab, outputMode, naeringFilter, classificationFilter, minePostnummer, debouncedSearch, vehicleFilter, selectedBrands, brandOrgClassification])

  // -------- Næring data fetch --------

  const NAERING_PAGE_SIZE = 100

  const fetchNaeringData = async (page: number = 1) => {
    const thisFetchId = ++naeringFetchId.current
    try {
      setLoading(true)
      setError(null)

      // Snapshot queried state
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate(activeDateRange.startDate)
      setQueriedEndDate(activeDateRange.endDate)
      setQueriedNaeringFilter(naeringFilter)
      setQueriedClassificationFilter(classificationFilter)

      // Compute date range
      let start_date: string
      let end_date: string
      if (outputMode === 'custom' && customStartDate && customEndDate) {
        start_date = customStartDate
        end_date = customEndDate
      } else {
        const range = getDateRangeForMode(outputMode, maxDataDate)
        start_date = range.startDate
        end_date = range.endDate
      }

      const prev_start = shiftYearBack(start_date)
      const prev_end = shiftYearBack(end_date)

      const varebilParam = getVarebilParam()

      const buildParams = (sd: string, ed: string, pg: number = 1, pgSize: number = NAERING_PAGE_SIZE, search?: string) => {
        const p: any = {
          start_date: sd,
          end_date: ed,
          page: pg,
          page_size: pgSize,
        }
        if (naeringFilter !== 'alle') p.naering_filter = naeringFilter
        if (classificationFilter) p.classification = classificationFilter
        if (minePostnummer) p.mine_postnummer = minePostnummer
        if (varebilParam !== undefined) p.varebil = varebilParam
        if (search) p.search = search
        return p
      }

      const classParams: any = { start_date, end_date }
      if (varebilParam !== undefined) classParams.varebil = varebilParam
      if (naeringFilter !== 'alle') classParams.naering_filter = naeringFilter
      if (minePostnummer) classParams.mine_postnummer = minePostnummer

      const parsePage = (res: any) => {
        if (res.data?.data && Array.isArray(res.data.data)) return res.data.data as any[]
        if (res.data?.items) return res.data.items as any[]
        if (Array.isArray(res.data)) return res.data as any[]
        return []
      }

      const searchParam = debouncedSearch || undefined

      // Fetch single page of current data
      const [currentRes, classRes] = await Promise.all([
        publicAPI.getOrganizationMarketShare(buildParams(start_date, end_date, page, NAERING_PAGE_SIZE, searchParam)),
        publicAPI.getOrganizationClassifications(classParams),
      ])

      const currentOrgs = parsePage(currentRes)
      const pagination = currentRes.data?.pagination
      const summary = currentRes.data?.summary

      // Update pagination state
      setNaeringPage(page)
      setNaeringTotalPages(pagination?.total_pages ?? 1)
      setNaeringTotalItems(pagination?.total_items ?? currentOrgs.length)
      setNaeringTotalVehicles(summary?.total_vehicles_in_period ?? currentOrgs.reduce((s: number, o: any) => s + toNumber(o.vehicle_count), 0))

      // For previous-year data: only fetch all pages on page 1 (filter/date change), reuse cache otherwise
      let currentPrevMap = prevYearMap
      if (page === 1) {
        const PREV_PAGE_SIZE = 500
        const newPrevMap = new Map<string, number>()
        try {
          let prevPage = 1
          let hasNext = true
          while (hasNext) {
            const prevRes = await publicAPI.getOrganizationMarketShare(buildParams(prev_start, prev_end, prevPage, PREV_PAGE_SIZE, searchParam))
            const prevItems = parsePage(prevRes)
            for (const o of prevItems) {
              newPrevMap.set(o.org_number, toNumber(o.vehicle_count))
            }
            hasNext = prevRes.data?.pagination?.has_next === true
            prevPage++
          }
        } catch {
          // No previous-year data — YoY columns will show N/A
        }
        setPrevYearMap(newPrevMap)
        currentPrevMap = newPrevMap
      }

      // Merge
      const merged: NaeringRow[] = currentOrgs.map((o: any, idx: number) => {
        const cur = toNumber(o.vehicle_count)
        const prev = currentPrevMap.get(o.org_number) ?? 0
        const change = cur - prev
        const changePct = prev > 0 ? ((change / prev) * 100) : (cur > 0 ? 100 : null)
        return {
          rank: o.rank ?? ((page - 1) * NAERING_PAGE_SIZE + idx + 1),
          org_number: o.org_number,
          org_name: o.org_name,
          short_name: o.short_name || '',
          classification: o.classification || '',
          current_count: cur,
          market_share_percentage: toNumber(o.market_share_percentage),
          previous_count: prev,
          change_count: change,
          change_percent: changePct,
        }
      })

      // Discard results if a newer fetch has been started
      if (thisFetchId !== naeringFetchId.current) return

      setNaeringRows(merged)

      const classData = classRes.data?.data || classRes.data || []
      setClassificationData(Array.isArray(classData) ? classData : [])
      setInitialLoad(false)
    } catch (err: any) {
      if (thisFetchId !== naeringFetchId.current) return
      console.error('Næring API error:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('analytics.errors.naering'))
    } finally {
      if (thisFetchId === naeringFetchId.current) {
        setLoading(false)
      }
    }
  }

  // -------- Brand / Model fetches (unchanged) --------

  const fetchBrandData = async () => {
    try {
      setBrandsLoading(true)
      setError(null)
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate(activeDateRange.startDate)
      setQueriedEndDate(activeDateRange.endDate)

      const start_date = activeDateRange.startDate
      const end_date = activeDateRange.endDate

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 50 }

      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await privateAPI.getCompanyCarsByBrand(params)
      const brands = (response.data.data || []) as CompanyCarBrandData[]
      const brandsWithRank = brands.map((brand, index) => ({
        ...brand,
        rank: index + 1
      }))
      setBrandData(brandsWithRank)
      setBrandSummary(response.data.summary || null)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('analytics.errors.brands'))
    } finally {
      setBrandsLoading(false)
    }
  }

  const fetchModelData = async () => {
    try {
      setModelsLoading(true)
      setError(null)
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate(activeDateRange.startDate)
      setQueriedEndDate(activeDateRange.endDate)

      const start_date = activeDateRange.startDate
      const end_date = activeDateRange.endDate

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 50 }

      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await privateAPI.getCompanyCarsByMasterModel(params)
      const models = (response.data.data || []) as CompanyCarModelData[]
      const modelsWithRank = models.map((model, index) => ({
        ...model,
        rank: index + 1
      }))
      setModelData(modelsWithRank)
      setModelSummary(response.data.summary || null)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('analytics.errors.models'))
    } finally {
      setModelsLoading(false)
    }
  }

  // -------- Brand Org tabs fetch (Merkekjøp / Merkeleasing) --------

  const fetchBrandOrgData = async (tab: BrandOrgTab, page: number = 1) => {
    if (selectedBrands.length === 0) return
    try {
      setBrandOrgLoading(true)
      setError(null)
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate(activeDateRange.startDate)
      setQueriedEndDate(activeDateRange.endDate)

      const start_date = activeDateRange.startDate
      const end_date = activeDateRange.endDate
      const varebilParam = getVarebilParam()

      const params: any = {
        brand: selectedBrands.join(','),
        start_date,
        end_date,
        page,
        page_size: 100,
      }
      if (varebilParam !== undefined) params.varebil = varebilParam
      if (minePostnummer) params.mine_postnummer = minePostnummer
      if (brandOrgClassification) params.classification = brandOrgClassification

      const apiFn = tab === 'brand_buyers' ? privateAPI.getBrandBuyers : tab === 'brand_leasers' ? privateAPI.getBrandLeasers : privateAPI.getBrandCombined
      const response = await apiFn(params)
      const respData = response.data

      setBrandOrgData(respData.data || [])
      setBrandOrgPage(respData.pagination?.page || 1)
      setBrandOrgTotalPages(respData.pagination?.total_pages || 0)
      setBrandOrgTotalItems(respData.pagination?.total_items || 0)
      setBrandOrgSummary(respData.summary || null)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('analytics.errors.brandOrg'))
    } finally {
      setBrandOrgLoading(false)
    }
  }

  // -------- Refresh handler --------

  const handleRefresh = () => {
    if (activeTab === 'organizations') fetchNaeringData(naeringPage)
    else if (activeTab === 'brands') fetchBrandData()
    else if (activeTab === 'models') fetchModelData()
    else if (BRAND_ORG_TABS.includes(activeTab as BrandOrgTab)) fetchBrandOrgData(activeTab as BrandOrgTab, brandOrgPage)
  }

  // -------- Search filter for Næring --------

  // Server-side search replaces client-side filtering
  const filteredNaeringRows = naeringRows

  // -------- Sorted data (useMemo — MUST be before early returns) --------

  const sortedNaeringRows = useMemo(() => {
    if (!orgSortConfig || !orgSortConfig.key || !orgSortConfig.direction) return filteredNaeringRows

    return [...filteredNaeringRows].sort((a, b) => {
      const key = orgSortConfig.key as keyof NaeringRow
      const aVal = a[key]
      const bVal = b[key]

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return orgSortConfig.direction === 'asc' ? cmp : -cmp
      }

      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)
      return orgSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [filteredNaeringRows, orgSortConfig])

  const naeringSummary = useMemo(() => {
    if (!naeringTotalItems) return null
    let totalPrevious = 0
    prevYearMap.forEach(v => { totalPrevious += v })
    const totalChange = naeringTotalVehicles - totalPrevious
    const totalChangePercent = totalPrevious > 0 ? ((totalChange / totalPrevious) * 100) : null
    return {
      current_count: naeringTotalVehicles,
      market_share_percentage: 100.0,
      previous_count: totalPrevious,
      change_count: totalChange,
      change_percent: totalChangePercent,
    }
  }, [naeringTotalItems, naeringTotalVehicles, prevYearMap])

  const sortedBrands = useMemo(() => {
    if (!brandSortConfig || !brandSortConfig.key || !brandSortConfig.direction) return brandData

    return [...brandData].sort((a, b) => {
      const key = brandSortConfig.key as keyof CompanyCarBrandData
      const aVal = a[key]
      const bVal = b[key]

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return brandSortConfig.direction === 'asc' ? cmp : -cmp
      }

      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)
      return brandSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [brandData, brandSortConfig])

  const sortedModels = useMemo(() => {
    if (!modelSortConfig || !modelSortConfig.key || !modelSortConfig.direction) return modelData

    return [...modelData].sort((a, b) => {
      const key = modelSortConfig.key as keyof CompanyCarModelData
      const aVal = a[key]
      const bVal = b[key]

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return modelSortConfig.direction === 'asc' ? cmp : -cmp
      }

      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)
      return modelSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [modelData, modelSortConfig])

  // -------- Early returns --------

  if (loading && initialLoad && activeTab === 'organizations') return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  // -------- Sort handlers --------

  const handleOrgSort = (key: keyof NaeringRow) => {
    setOrgSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getOrgSortIcon = (key: keyof NaeringRow) => {
    if (!orgSortConfig || orgSortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (orgSortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  const handleBrandSort = (key: keyof CompanyCarBrandData) => {
    setBrandSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getBrandSortIcon = (key: keyof CompanyCarBrandData) => {
    if (!brandSortConfig || brandSortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (brandSortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  const handleModelSort = (key: keyof CompanyCarModelData) => {
    setModelSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getModelSortIcon = (key: keyof CompanyCarModelData) => {
    if (!modelSortConfig || modelSortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (modelSortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  // -------- Column headers --------
  const periodHeaderKeys = getPeriodColumnHeaders(outputMode)
  const periodHeaders = { current: t(periodHeaderKeys.current), previous: t(periodHeaderKeys.previous) }

  // -------- Export columns --------

  const orgColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('analytics.columns.rank') },
    { key: 'org_name', label: t('analytics.columns.aktor') },
    { key: 'short_name', label: t('analytics.columns.type'), format: (val, row) => (val as string) || classificationShortCodes[(row as any)?.classification] || (row as any)?.classification || '' },
    { key: 'current_count', label: periodHeaders.current, format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'market_share_percentage', label: t('analytics.columns.andel'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'previous_count', label: periodHeaders.previous, format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'change_count', label: t('analytics.columns.endring'), format: (val) => { const n = toNumber(val); return n > 0 ? `+${n.toLocaleString('nb-NO')}` : n.toLocaleString('nb-NO') } },
    { key: 'change_percent', label: t('analytics.columns.endringPct'), format: (val) => { const n = toNumber(val); return n !== null && n !== 0 ? n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %' : '—' } },
  ]

  const brandColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('analytics.columns.rank') },
    { key: 'brand', label: t('analytics.columns.merke') },
    { key: 'total_company_cars', label: t('analytics.columns.firmabiler'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'owned_percentage', label: t('analytics.columns.eidPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'leased_percentage', label: t('analytics.columns.leasetPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'private_leasing_percentage', label: t('analytics.columns.privatLeasingPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'company_leasing_percentage', label: t('analytics.columns.naeringsleasingPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
  ]

  const modelColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('analytics.columns.rank') },
    { key: 'brand', label: t('analytics.columns.merke') },
    { key: 'master_model', label: t('analytics.columns.modell') },
    { key: 'total_company_cars', label: t('analytics.columns.firmabiler'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'owned_percentage', label: t('analytics.columns.eidPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'leased_percentage', label: t('analytics.columns.leasetPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'private_leasing_percentage', label: t('analytics.columns.privatLeasingPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
    { key: 'company_leasing_percentage', label: t('analytics.columns.naeringsleasingPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0,0' },
  ]

  const brandOrgExportColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('analytics.columns.rank') },
    { key: 'org_name', label: t('analytics.columns.organisasjon') },
    { key: 'classification', label: t('analytics.columns.type'), format: (val) => classificationShortCodes[val as string] || (val as string) || '' },
    ...(activeTab === 'brand_combined' ? [
      { key: 'owned_count', label: t('analytics.columns.kjopt'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
      { key: 'leased_count', label: t('analytics.columns.leaset'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    ] as ColumnDefinition[] : []),
    { key: 'vehicle_count', label: activeTab === 'brand_combined' ? t('analytics.columns.totalt') : t('analytics.columns.antall'), format: (val) => toNumber(val)?.toLocaleString('nb-NO') || '0' },
    { key: 'brand_market_share_pct', label: t('analytics.columns.merkeandelPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '' },
    { key: 'org_brand_share_pct', label: t('analytics.columns.orgAndelPct'), format: (val) => toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '' },
    { key: 'top_3_models', label: t('analytics.columns.topp3'), format: (val) => Array.isArray(val) ? (val as BrandOrgTopModel[]).map(m => `${m.model_name} (${m.count})`).join(', ') : '' },
    { key: 'yoy_change', label: t('analytics.columns.endring'), format: (val) => val != null ? String(val) : '' },
    { key: 'yoy_change_pct', label: t('analytics.columns.endringPct'), format: (val) => val != null ? toNumber(val)?.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %' : '—' },
  ]

  // ========================================
  // JSX
  // ========================================

  return (
    <div className="space-y-6">
      {/* Header — dynamic based on næring filter */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('analytics.title', { label: t(NAERING_HEADER_LABELS[naeringFilter]) })}
        </h1>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('sourceData')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
        <button
          onClick={() => setActiveTab('organizations')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'organizations'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4" />
            {t('analytics.tabs.naering')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brands'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('analytics.tabs.brands')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('models')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'models'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t('analytics.tabs.models')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('brand_buyers')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brand_buyers'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('analytics.tabs.brandBuyers')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('brand_leasers')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brand_leasers'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('analytics.tabs.brandLeasers')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('brand_combined')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brand_combined'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('analytics.tabs.brandCombined')}
          </span>
        </button>
      </div>

      {/* ================================================ */}
      {/* Næring Tab */}
      {/* ================================================ */}
      {activeTab === 'organizations' && (
      <>
        {/* Output Mode selector */}
        <div className="card p-4">
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(OUTPUT_MODE_LABELS) as OutputMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setOutputMode(mode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputMode === mode
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {t(OUTPUT_MODE_LABELS[mode])}
              </button>
            ))}
            {outputMode === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('analytics.filters.startDate')}
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      const val = e.target.value
                      setCustomStartDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                    }}
                    max={maxDataDate || undefined}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('analytics.filters.endDate')}
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      const val = e.target.value
                      setCustomEndDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                    }}
                    max={maxDataDate || undefined}
                    className="input-field"
                  />
                </div>
                <button
                  onClick={() => { setNaeringPage(1); fetchNaeringData(1) }}
                  className="btn-primary self-end"
                  disabled={!customStartDate || !customEndDate}
                >
                  {t('analytics.filters.apply')}
                </button>
              </>
            )}
            <div className="ml-auto">
              <button
                onClick={handleRefresh}
                className={`btn-primary text-sm ${outputMode === 'custom' && (!customStartDate || !customEndDate) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={outputMode === 'custom' && (!customStartDate || !customEndDate)}
              >
                {t('analytics.filters.refresh')}
              </button>
            </div>
          </div>

          {/* Postal Code Filter - inside period card */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
            <PostnummerFilter
              enabled={postnummerFilterEnabled}
              onToggle={setPostnummerFilterEnabled}
              compact
            />
          </div>
        </div>

        {/* Period Info Banner */}
        {/* <PeriodInfoBanner
          maxDataDate={maxDataDate}
          outputMode={outputMode}
          periodLabel={t(OUTPUT_MODE_LABELS[outputMode])}
          startDate={activeDateRange.startDate}
          endDate={activeDateRange.endDate}
        /> */}

        {/* Query Explanation Box */}
        <QueryExplanationBox
          maxDataDate={maxDataDate}
          outputMode={queriedOutputMode}
          vehicleFilter={queriedVehicleFilter}
          postnummerFilter={queriedPostnummer}
          periodLabel={t(OUTPUT_MODE_LABELS[queriedOutputMode])}
          startDate={queriedStartDate}
          endDate={queriedEndDate}
          extraWarnings={[
            ...(queriedNaeringFilter === 'leasing' ? [t('analytics.warnings.leasing')] : queriedNaeringFilter === 'eier_og_leasingtaker' ? [t('analytics.warnings.eierOgLeasingtaker')] : []),
            ...(queriedClassificationFilter ? [t('analytics.warnings.classificationBox', { label: classificationLabel(queriedClassificationFilter) })] : []),
            ...(queriedPostnummer ? [queriedNaeringFilter === 'leasing' ? t('analytics.warnings.postnrLeasing') : queriedNaeringFilter === 'eier_og_leasingtaker' ? t('analytics.warnings.postnrCombined') : t('analytics.warnings.postnrOwner')] : []),
          ]}
        />

        {/* Classification card + Næring filter */}
        <div className="card overflow-x-auto">
          <div className="p-6 pb-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('analytics.classificationCard.title')}
            </h2>
          </div>
          <p className="px-6 pt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('analytics.classificationCard.hint')}
          </p>

          {/* Næring filter buttons */}
          <div className="px-6 pt-3 pb-4 flex gap-2">
            {(Object.keys(NAERING_FILTER_LABELS) as NaeringFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setNaeringFilter(f)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  naeringFilter === f
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {t(NAERING_FILTER_LABELS[f])}
              </button>
            ))}
          </div>

          {/* Classification table */}
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-left">{t('analytics.classificationCard.aktortype')}</th>
                <th className="text-right">{t('analytics.classificationCard.antallBiler')}</th>
                <th className="text-right">{t('analytics.classificationCard.andel')}</th>
              </tr>
            </thead>
            <tbody>
              {classificationData.map((org, index) => {
                const isActive = classificationFilter === org.classification
                return (
                <tr
                  key={`${org.classification}-${index}`}
                  onClick={() => setClassificationFilter(isActive ? null : org.classification)}
                  className={`cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    isActive ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-300 dark:ring-primary-700' : ''
                  }`}
                >
                  <td className="font-semibold">
                    {classificationLabel(org.classification)}
                    {isActive && <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">{t('analytics.classificationCard.active')}</span>}
                  </td>
                  <td className="text-right">
                    {toNumber(org.count).toLocaleString('nb-NO')}
                  </td>
                  <td className="text-right">
                    {toNumber(org.percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
          {classificationFilter && (
            <div className="px-6 py-2 bg-primary-50 dark:bg-primary-900/20 border-t border-primary-200 dark:border-primary-800">
              <button
                onClick={() => setClassificationFilter(null)}
                className="text-sm text-primary-700 dark:text-primary-300 hover:underline"
              >
                {t('analytics.classificationCard.clearFilter', { label: classificationLabel(classificationFilter) })}
              </button>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="card p-4">
          <div className="flex gap-2 items-center">
            <div className="flex-1" onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch() }}>
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('analytics.search.placeholder')}
              />
            </div>
            <button
              onClick={triggerSearch}
              disabled={loading && !initialLoad}
              className="btn-primary text-sm whitespace-nowrap"
            >
              {t('analytics.search.button')}
            </button>
            {debouncedSearch && (
              <button
                onClick={clearSearch}
                disabled={loading && !initialLoad}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {loading && !initialLoad && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('analytics.search.reset')}
              </button>
            )}
          </div>
        </div>

        {/* Export and Clear Sorting */}
        <div className="flex justify-between items-center mb-4">
          <div>
            {orgSortConfig && orgSortConfig.key && (
              <div className="card px-4 py-2">
                <button
                  onClick={() => setOrgSortConfig(null)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  {t('analytics.resetSort')}
                </button>
              </div>
            )}
          </div>
          <ExportButtons
            data={sortedNaeringRows}
            columns={orgColumns}
            metadata={{
              title: t('analytics.title', { label: t(NAERING_HEADER_LABELS[naeringFilter]) }),
              dateRange: `${new Date(activeDateRange.startDate).toLocaleDateString('nb-NO')} - ${new Date(activeDateRange.endDate).toLocaleDateString('nb-NO')}`,
              filters: [
                ...(minePostnummer ? [t('analytics.exportFilters.postnummer', { value: minePostnummer })] : []),
                ...(searchTerm ? [t('analytics.exportFilters.search', { value: searchTerm })] : []),
                t('analytics.exportFilters.page', { page: naeringPage, pages: naeringTotalPages, total: naeringTotalItems }),
              ].filter(f => f.length > 0),
              explanationText: buildExplanationText({
                maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                postnummerFilter: queriedPostnummer, periodLabel: t(OUTPUT_MODE_LABELS[queriedOutputMode]),
                startDate: queriedStartDate, endDate: queriedEndDate,
                extraWarnings: [
                  ...(queriedNaeringFilter === 'leasing' ? [t('analytics.warnings.leasing')] : queriedNaeringFilter === 'eier_og_leasingtaker' ? [t('analytics.warnings.eierOgLeasingtaker')] : []),
                  ...(queriedClassificationFilter ? [t('analytics.warnings.classificationExport', { label: classificationLabel(queriedClassificationFilter) })] : []),
                  ...(queriedPostnummer ? [queriedNaeringFilter === 'leasing' ? t('analytics.warnings.postnrLeasing') : queriedNaeringFilter === 'eier_og_leasingtaker' ? t('analytics.warnings.postnrCombined') : t('analytics.warnings.postnrOwner')] : []),
                ],
              }),
            }}
          />
        </div>

        {/* YoY Comparison Grid */}
        <div className="card overflow-x-auto relative">
          {loading && !initialLoad && (
            <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70 z-10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          )}
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th
                  className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('rank')}
                  title={t('analytics.columns.rankTitle')}
                >
                  <div className="flex items-center justify-center">
                    <span>{t('analytics.columns.rank')}</span>
                    {getOrgSortIcon('rank')}
                  </div>
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('org_name')}
                >
                  <div className="flex items-center justify-between">
                    <span>{t('analytics.columns.aktor')}</span>
                    {getOrgSortIcon('org_name')}
                  </div>
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('short_name')}
                >
                  <div className="flex items-center justify-between">
                    <span>{t('analytics.columns.type')}</span>
                    {getOrgSortIcon('short_name')}
                  </div>
                </th>
                <th
                  className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('current_count')}
                >
                  <div className="flex items-center justify-end">
                    <span>{periodHeaders.current}</span>
                    {getOrgSortIcon('current_count')}
                  </div>
                </th>
                <th
                  className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('market_share_percentage')}
                >
                  <div className="flex items-center justify-end">
                    <span>{t('analytics.columns.andel')}</span>
                    {getOrgSortIcon('market_share_percentage')}
                  </div>
                </th>
                <th
                  className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('previous_count')}
                >
                  <div className="flex items-center justify-end">
                    <span>{periodHeaders.previous}</span>
                    {getOrgSortIcon('previous_count')}
                  </div>
                </th>
                <th
                  className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('change_count')}
                >
                  <div className="flex items-center justify-end">
                    <span>{t('analytics.columns.endring')}</span>
                    {getOrgSortIcon('change_count')}
                  </div>
                </th>
                <th
                  className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleOrgSort('change_percent')}
                >
                  <div className="flex items-center justify-end">
                    <span>{t('analytics.columns.endringPct')}</span>
                    {getOrgSortIcon('change_percent')}
                  </div>
                </th>
                <th className="text-right">{t('analytics.columns.detaljer')}</th>
              </tr>
            </thead>
            <tbody>
              {naeringSummary && (
                <tr className="bg-gray-50 dark:bg-gray-800/40 font-bold border-t border-b border-gray-300 dark:border-gray-600">
                  <td className="text-center py-2">
                    <span className="text-gray-900 dark:text-gray-100 font-bold">∑</span>
                  </td>
                  <td className="font-bold text-gray-900 dark:text-gray-100 py-2">{t('analytics.columns.total')}</td>
                  <td className="py-2"></td>
                  <td className="text-right font-bold py-2">{naeringSummary.current_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right py-2">
                    <span className="font-bold text-gray-900 dark:text-gray-100">100,0</span>
                  </td>
                  <td className="text-right font-bold py-2">{naeringSummary.previous_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right py-2">
                    <ChangeCount value={naeringSummary.change_count} />
                  </td>
                  <td className="text-right py-2">
                    <ChangeIndicator value={naeringSummary.change_percent} showIcon={false} />
                  </td>
                  <td className="py-2"></td>
                </tr>
              )}
              {sortedNaeringRows.map((row) => (
                <tr key={row.org_number}>
                  <td className="text-center text-gray-700 dark:text-gray-300">
                    {row.rank}
                  </td>
                  <td className="font-semibold max-w-xs truncate" title={formatLeasingselskap(row.org_name)}>
                    {formatLeasingselskap(row.org_name)}
                  </td>
                  <td>
                    <span
                      className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-sm cursor-help"
                      title={classificationLabel(row.classification)}
                    >
                      {row.short_name || classificationShortCodes[row.classification] || row.classification}
                    </span>
                  </td>
                  <td className="text-right font-medium">
                    {row.current_count.toLocaleString('nb-NO')}
                  </td>
                  <td className="text-right">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {row.market_share_percentage.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </span>
                  </td>
                  <td className="text-right">
                    {row.previous_count.toLocaleString('nb-NO')}
                  </td>
                  <td className="text-right">
                    <ChangeCount value={row.change_count} />
                  </td>
                  <td className="text-right">
                    <ChangeIndicator value={row.change_percent} showIcon={false} />
                  </td>
                  <td className="text-right">
                    <Link
                      to={(() => {
                        const params = new URLSearchParams({
                          from: queriedStartDate,
                          to: queriedEndDate,
                        })
                        if (queriedVehicleFilter && queriedVehicleFilter !== 'alle') {
                          params.set('vehicleFilter', queriedVehicleFilter)
                        }
                        if (queriedClassificationFilter) {
                          params.set('classification', queriedClassificationFilter)
                        }
                        if (queriedPostnummer) {
                          params.set('mine_postnummer', queriedPostnummer)
                        }
                        return `/organizations/aktor/${row.org_number}?${params.toString()}`
                      })()}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm whitespace-nowrap"
                    >
                      {t('analytics.columns.detaljerLink')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {naeringTotalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('analytics.pagination.showing', { from: ((naeringPage - 1) * 100) + 1, to: Math.min(naeringPage * 100, naeringTotalItems), total: naeringTotalItems.toLocaleString('nb-NO') })}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => goToNaeringPage(1)} disabled={naeringPage === 1 || loading} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">«</button>
                <button onClick={() => goToNaeringPage(naeringPage - 1)} disabled={naeringPage === 1 || loading} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">{t('analytics.pagination.prev')}</button>
                <span className="text-sm text-gray-700 dark:text-gray-300 px-2">{t('analytics.pagination.pageOf', { page: naeringPage, pages: naeringTotalPages })}</span>
                <button onClick={() => goToNaeringPage(naeringPage + 1)} disabled={naeringPage === naeringTotalPages || loading} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">{t('analytics.pagination.next')}</button>
                <button onClick={() => goToNaeringPage(naeringTotalPages)} disabled={naeringPage === naeringTotalPages || loading} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">»</button>
              </div>
            </div>
          )}

          {sortedNaeringRows.length === 0 && (
            <div className="p-12 text-center">
              <Building2 className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {t('analytics.empty.noOrgs')}
              </p>
            </div>
          )}
        </div>
      </>
      )}

      {/* ================================================ */}
      {/* Brands Tab */}
      {/* ================================================ */}
      {activeTab === 'brands' && (
        <>
          {/* Output Mode selector */}
          <div className="card p-4">
            <div className="flex flex-wrap items-center gap-2">
              {(Object.keys(OUTPUT_MODE_LABELS) as OutputMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setOutputMode(mode)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    outputMode === mode
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(OUTPUT_MODE_LABELS[mode])}
                </button>
              ))}
              {outputMode === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('analytics.filters.startDate')}
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => {
                        const val = e.target.value
                        setCustomStartDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                      }}
                      max={maxDataDate || undefined}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('analytics.filters.endDate')}
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => {
                        const val = e.target.value
                        setCustomEndDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                      }}
                      max={maxDataDate || undefined}
                      className="input-field"
                    />
                  </div>
                  <button
                    onClick={() => fetchBrandData()}
                    className="btn-primary self-end"
                    disabled={!customStartDate || !customEndDate}
                  >
                    {t('analytics.filters.apply')}
                  </button>
                </>
              )}
              <div className="ml-auto">
                <button
                  onClick={handleRefresh}
                  className={`btn-primary text-sm ${outputMode === 'custom' && (!customStartDate || !customEndDate) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={outputMode === 'custom' && (!customStartDate || !customEndDate)}
                >
                  {t('analytics.filters.refresh')}
                </button>
              </div>
            </div>

            {/* Postal Code Filter */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
              <PostnummerFilter
                enabled={postnummerFilterEnabled}
                onToggle={setPostnummerFilterEnabled}
                compact
              />
            </div>
          </div>

          {/* Query Explanation Box */}
          <QueryExplanationBox
            maxDataDate={maxDataDate}
            outputMode={queriedOutputMode}
            vehicleFilter={queriedVehicleFilter}
            postnummerFilter={queriedPostnummer}
            periodLabel={t(OUTPUT_MODE_LABELS[queriedOutputMode])}
            startDate={queriedStartDate}
            endDate={queriedEndDate}
          />

          {!brandsLoading && brandData.length === 0 ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('analytics.empty.noData')}
            </div>
          ) : (
            <>
              {/* Export and Clear Sorting */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {brandSortConfig && brandSortConfig.key && (
                    <div className="card px-4 py-2">
                      <button
                        onClick={() => setBrandSortConfig(null)}
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                      >
                        {t('analytics.resetSort')}
                      </button>
                    </div>
                  )}
                </div>
                <ExportButtons
                  data={brandSummary ? [{ ...brandSummary, rank: '∑', brand: t('analytics.columns.total') }, ...sortedBrands] : sortedBrands}
                  columns={brandColumns}
                  metadata={{
                    title: t('analytics.title', { label: t('analytics.tabs.brands') }),
                    dateRange: `${new Date(activeDateRange.startDate).toLocaleDateString('nb-NO')} - ${new Date(activeDateRange.endDate).toLocaleDateString('nb-NO')}`,
                    filters: minePostnummer ? [t('analytics.exportFilters.postnummer', { value: minePostnummer })] : undefined,
                    explanationText: buildExplanationText({
                      maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                      postnummerFilter: queriedPostnummer, periodLabel: t(OUTPUT_MODE_LABELS[queriedOutputMode]),
                      startDate: queriedStartDate, endDate: queriedEndDate,
                    }),
                  }}
                />
              </div>

            <div className="card overflow-x-auto relative">
              {brandsLoading && (
                <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70 z-10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                </div>
              )}
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th
                      className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('rank')}
                      title={t('analytics.columns.originalRanking')}
                    >
                      <div className="flex items-center justify-center">
                        <span>{t('analytics.columns.rank')}</span>
                        {getBrandSortIcon('rank')}
                      </div>
                    </th>
                    <th
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('brand')}
                    >
                      <div className="flex items-center justify-between">
                        <span>{t('analytics.columns.merke')}</span>
                        {getBrandSortIcon('brand')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('total_company_cars')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.firmabiler')}</span>
                        {getBrandSortIcon('total_company_cars')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('owned_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.eid')}</span>
                        {getBrandSortIcon('owned_percentage')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('leased_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.leaset')}</span>
                        {getBrandSortIcon('leased_percentage')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('private_leasing_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.privatLeasing')}</span>
                        {getBrandSortIcon('private_leasing_percentage')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleBrandSort('company_leasing_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.naeringsleasing')}</span>
                        {getBrandSortIcon('company_leasing_percentage')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {brandSummary && <CompanyCarTotalRow summary={brandSummary} label={t('analytics.columns.total')} />}
                  {sortedBrands.map((brand, index) => (
                    <tr key={`${brand.brand}-${index}`} className={activeMerker.has(brand.brand) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : ''}>
                      <td className="text-center font-bold text-gray-900 dark:text-gray-100">
                        {brand.rank}
                      </td>
                      <td className="font-semibold">{brand.brand}</td>
                      <td className="text-right">{toNumber(brand.total_company_cars).toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(brand.owned_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(brand.owned_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(brand.leased_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(brand.leased_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(brand.private_leasing_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(brand.private_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(brand.company_leasing_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(brand.company_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}

      {/* ================================================ */}
      {/* Models Tab */}
      {/* ================================================ */}
      {activeTab === 'models' && (
        <>
          {/* Output Mode selector */}
          <div className="card p-4">
            <div className="flex flex-wrap items-center gap-2">
              {(Object.keys(OUTPUT_MODE_LABELS) as OutputMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setOutputMode(mode)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    outputMode === mode
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(OUTPUT_MODE_LABELS[mode])}
                </button>
              ))}
              {outputMode === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('analytics.filters.startDate')}
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => {
                        const val = e.target.value
                        setCustomStartDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                      }}
                      max={maxDataDate || undefined}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('analytics.filters.endDate')}
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => {
                        const val = e.target.value
                        setCustomEndDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                      }}
                      max={maxDataDate || undefined}
                      className="input-field"
                    />
                  </div>
                  <button
                    onClick={() => fetchModelData()}
                    className="btn-primary self-end"
                    disabled={!customStartDate || !customEndDate}
                  >
                    {t('analytics.filters.apply')}
                  </button>
                </>
              )}
              <div className="ml-auto">
                <button
                  onClick={handleRefresh}
                  className={`btn-primary text-sm ${outputMode === 'custom' && (!customStartDate || !customEndDate) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={outputMode === 'custom' && (!customStartDate || !customEndDate)}
                >
                  {t('analytics.filters.refresh')}
                </button>
              </div>
            </div>

            {/* Postal Code Filter */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
              <PostnummerFilter
                enabled={postnummerFilterEnabled}
                onToggle={setPostnummerFilterEnabled}
                compact
              />
            </div>
          </div>

          {/* Query Explanation Box */}
          <QueryExplanationBox
            maxDataDate={maxDataDate}
            outputMode={queriedOutputMode}
            vehicleFilter={queriedVehicleFilter}
            postnummerFilter={queriedPostnummer}
            periodLabel={t(OUTPUT_MODE_LABELS[queriedOutputMode])}
            startDate={queriedStartDate}
            endDate={queriedEndDate}
          />

          {!modelsLoading && modelData.length === 0 ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('analytics.empty.noData')}
            </div>
          ) : (
            <>
              {/* Export and Clear Sorting */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {modelSortConfig && modelSortConfig.key && (
                    <div className="card px-4 py-2">
                      <button
                        onClick={() => setModelSortConfig(null)}
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                      >
                        {t('analytics.resetSort')}
                      </button>
                    </div>
                  )}
                </div>
                <ExportButtons
                  data={modelSummary ? [{ ...modelSummary, rank: '∑', brand: t('analytics.columns.total'), master_model: '' }, ...sortedModels] : sortedModels}
                  columns={modelColumns}
                  metadata={{
                    title: t('analytics.title', { label: t('analytics.tabs.models') }),
                    dateRange: `${new Date(activeDateRange.startDate).toLocaleDateString('nb-NO')} - ${new Date(activeDateRange.endDate).toLocaleDateString('nb-NO')}`,
                    filters: minePostnummer ? [t('analytics.exportFilters.postnummer', { value: minePostnummer })] : undefined,
                    explanationText: buildExplanationText({
                      maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                      postnummerFilter: queriedPostnummer, periodLabel: t(OUTPUT_MODE_LABELS[queriedOutputMode]),
                      startDate: queriedStartDate, endDate: queriedEndDate,
                    }),
                  }}
                />
              </div>

            <div className="card overflow-x-auto relative">
              {modelsLoading && (
                <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70 z-10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                </div>
              )}
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th
                      className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('rank')}
                      title={t('analytics.columns.originalRanking')}
                    >
                      <div className="flex items-center justify-center">
                        <span>{t('analytics.columns.rank')}</span>
                        {getModelSortIcon('rank')}
                      </div>
                    </th>
                    <th
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('brand')}
                    >
                      <div className="flex items-center justify-between">
                        <span>{t('analytics.columns.merke')}</span>
                        {getModelSortIcon('brand')}
                      </div>
                    </th>
                    <th
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('master_model')}
                    >
                      <div className="flex items-center justify-between">
                        <span>{t('analytics.columns.modell')}</span>
                        {getModelSortIcon('master_model')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('total_company_cars')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.firmabiler')}</span>
                        {getModelSortIcon('total_company_cars')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('owned_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.eid')}</span>
                        {getModelSortIcon('owned_percentage')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('leased_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.leaset')}</span>
                        {getModelSortIcon('leased_percentage')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('private_leasing_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.privatLeasing')}</span>
                        {getModelSortIcon('private_leasing_percentage')}
                      </div>
                    </th>
                    <th
                      className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleModelSort('company_leasing_percentage')}
                    >
                      <div className="flex items-center justify-end">
                        <span>{t('analytics.columns.naeringsleasing')}</span>
                        {getModelSortIcon('company_leasing_percentage')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {modelSummary && <CompanyCarTotalRow summary={modelSummary} label={t('analytics.columns.total')} blankCells={1} />}
                  {sortedModels.map((model, index) => (
                    <tr key={`${model.brand}-${model.master_model}-${index}`}>
                      <td className="text-center font-bold text-gray-900 dark:text-gray-100">
                        {model.rank}
                      </td>
                      <td className="font-semibold">{model.brand}</td>
                      <td>{model.master_model}</td>
                      <td className="text-right">{toNumber(model.total_company_cars).toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(model.owned_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.owned_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(model.leased_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.leased_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(model.private_leasing_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.private_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(model.company_leasing_percentage).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(model.company_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}

      {/* ================================================ */}
      {/* Merkekjøp / Merkeleasing / Merkekjøp og -leasing Tab */}
      {/* ================================================ */}
      {BRAND_ORG_TABS.includes(activeTab as BrandOrgTab) && (
      <>
        {/* Filters — match Nybilstatistikk layout */}
        <div className="card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('analytics.brandOrg.periode')}</span>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(OUTPUT_MODE_LABELS) as OutputMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => setOutputMode(mode)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    outputMode === mode
                      ? 'bg-primary-600 text-white shadow-xs'
                      : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                  }`}
                >
                  {t(OUTPUT_MODE_LABELS[mode])}
                </button>
              ))}
            </div>
            {outputMode === 'custom' && (
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('analytics.filters.startDate')}</label>
                  <input type="date" value={customStartDate} onChange={e => {
                    const val = e.target.value
                    setCustomStartDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                  }} max={maxDataDate || undefined} className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('analytics.filters.endDate')}</label>
                  <input type="date" value={customEndDate} onChange={e => {
                    const val = e.target.value
                    setCustomEndDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                  }} max={maxDataDate || undefined} className="input-field" />
                </div>
              </div>
            )}
            <div className="ml-auto">
              <button
                onClick={() => selectedBrands.length > 0 && fetchBrandOrgData(activeTab as BrandOrgTab, 1)}
                disabled={selectedBrands.length === 0 || (outputMode === 'custom' && (!customStartDate || !customEndDate))}
                className={`btn-primary text-sm ${selectedBrands.length === 0 || (outputMode === 'custom' && (!customStartDate || !customEndDate)) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t('analytics.filters.refresh')}
              </button>
            </div>
          </div>

          {/* Postal code filter — inside same card, separated by border */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
            <PostnummerFilter
              enabled={postnummerFilterEnabled}
              onToggle={setPostnummerFilterEnabled}
              compact
            />
          </div>
        </div>

        {/* Brand picker + Kategori */}
        <div className="card p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('analytics.brandOrg.velgMerke')}
              </label>
              <select
                value={selectedBrands[0] || ''}
                onChange={(e) => {
                  const v = e.target.value
                  setSelectedBrands(v ? [v] : [])
                }}
                className="input-field min-w-[220px]"
              >
                <option value="">{t('analytics.brandOrg.velgMerkeOption')}</option>
                {availableBrands.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('analytics.brandOrg.kategori')}
              </label>
              <select
                value={brandOrgClassification}
                onChange={e => setBrandOrgClassification(e.target.value)}
                className="input-field min-w-[220px]"
              >
                <option value="">{t('analytics.brandOrg.alleKategorier')}</option>
                <option value="Leasingselskap">{t('analytics.classification.leasingselskap')}</option>
                <option value="bilbransje">{t('analytics.classification.bilbransje')}</option>
                <option value="bilutleieselskap">{t('analytics.classification.bilutleieselskap')}</option>
                <option value="kommune">{t('analytics.classification.kommune')}</option>
                <option value="other_organization">{t('analytics.classification.otherOrganization')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results grid */}
        {brandOrgLoading ? (
          <LoadingSpinner />
        ) : brandOrgData.length > 0 ? (
          <>
            <div className="flex justify-end mb-2">
              <ExportButtons
                data={[
                  ...(brandOrgSummary ? [{
                    rank: '∑',
                    org_name: t('analytics.columns.total'),
                    classification: '',
                    owned_count: brandOrgSummary.total_owned,
                    leased_count: brandOrgSummary.total_leased,
                    vehicle_count: brandOrgSummary.total_vehicles,
                    brand_market_share_pct: 100,
                    org_brand_share_pct: null,
                    top_3_models: brandOrgSummary.top_3_models,
                    yoy_change: brandOrgSummary.total_yoy_change,
                    yoy_change_pct: brandOrgSummary.total_yoy_change_pct,
                  }] : []),
                  ...brandOrgData,
                ]}
                columns={brandOrgExportColumns}
                metadata={{
                  title: t('analytics.titleWithBrands', { label: t(BRAND_ORG_TAB_LABELS[activeTab as BrandOrgTab]), brands: selectedBrands.join(', ') }),
                  dateRange: `${new Date(queriedStartDate).toLocaleDateString('nb-NO')} – ${new Date(queriedEndDate).toLocaleDateString('nb-NO')}`,
                  filters: (() => {
                    const f: string[] = []
                    if (queriedPostnummer) f.push(t('analytics.exportFilters.postnummer', { value: queriedPostnummer }))
                    if (brandOrgClassification) f.push(t('analytics.exportFilters.kategori', { value: classificationLabel(brandOrgClassification) }))
                    return f.length ? f : undefined
                  })(),
                }}
              />
            </div>
            <div className="card overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="text-center" title={t('analytics.columns.rankTitle')}>{t('analytics.columns.rank')}</th>
                  <th title={t('analytics.columnTitles.organisasjon')}>{t('analytics.columns.organisasjon')}</th>
                  <th title={t('analytics.columnTitles.type')}>{t('analytics.columns.type')}</th>
                  {activeTab === 'brand_combined' && (
                    <>
                      <th className="text-right" title={t('analytics.columnTitles.kjopt')}>{t('analytics.columns.kjopt')}</th>
                      <th className="text-right" title={t('analytics.columnTitles.leaset')}>{t('analytics.columns.leaset')}</th>
                    </>
                  )}
                  <th className="text-right" title={activeTab === 'brand_combined' ? t('analytics.columnTitles.totalt') : t('analytics.columnTitles.antall')}>{activeTab === 'brand_combined' ? t('analytics.columns.totalt') : t('analytics.columns.antall')}</th>
                  <th className="text-right" title={t('analytics.columnTitles.merkeandel')}>{t('analytics.columns.merkeandel')}</th>
                  <th className="text-right" title={t('analytics.columnTitles.orgAndel')}>{t('analytics.columns.orgAndel')}</th>
                  <th title={t('analytics.columnTitles.topp3')}>{t('analytics.columns.topp3')}</th>
                  <th className="text-right" title={t('analytics.columnTitles.endring')}>{t('analytics.columns.endring')}</th>
                  <th className="text-right" title={t('analytics.columnTitles.endringPct')}>{t('analytics.columns.endringPct')}</th>
                </tr>
              </thead>
              <tbody>
                {brandOrgSummary && (
                  <tr className="bg-gray-50 dark:bg-gray-800/40 font-bold border-t border-b border-gray-300 dark:border-gray-600">
                    <td className="text-center font-bold">∑</td>
                    <td className="font-bold text-gray-900 dark:text-gray-100">{t('analytics.columns.total')}</td>
                    <td></td>
                    {activeTab === 'brand_combined' && (
                      <>
                        <td className="text-right font-bold">{(brandOrgSummary.total_owned ?? 0).toLocaleString('nb-NO')}</td>
                        <td className="text-right font-bold">{(brandOrgSummary.total_leased ?? 0).toLocaleString('nb-NO')}</td>
                      </>
                    )}
                    <td className="text-right font-bold">{brandOrgSummary.total_vehicles.toLocaleString('nb-NO')}</td>
                    <td className="text-right font-bold">{(100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                    <td></td>
                    <td className="text-gray-600 dark:text-gray-400">
                      {brandOrgSummary.top_3_models.map(m => `${m.model_name} (${m.count})`).join(', ')}
                    </td>
                    <td className="text-right">
                      <ChangeCountShared value={brandOrgSummary.total_yoy_change} />
                    </td>
                    <td className="text-right">
                      <ChangeIndicatorShared value={brandOrgSummary.total_yoy_change_pct} />
                    </td>
                  </tr>
                )}
                {brandOrgData.map(row => (
                  <tr key={row.org_number + (row.brand || '')}>
                    <td className="text-center text-gray-500">{row.rank}</td>
                    <td className="font-medium text-gray-900 dark:text-gray-100" title={formatLeasingselskap(row.org_name)}>{formatLeasingselskap(row.org_name)}</td>
                    <td>
                      <span className="px-2 py-0.5 text-xs rounded-sm bg-gray-100 dark:bg-dark-border text-gray-700 dark:text-gray-300" title={row.classification}>
                        {classificationShortCodes[row.classification] || row.classification}
                      </span>
                    </td>
                    {activeTab === 'brand_combined' && (
                      <>
                        <td className="text-right">{(row.owned_count ?? 0).toLocaleString('nb-NO')}</td>
                        <td className="text-right">{(row.leased_count ?? 0).toLocaleString('nb-NO')}</td>
                      </>
                    )}
                    <td className="text-right">{row.vehicle_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right">{row.brand_market_share_pct.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                    <td className="text-right">{row.org_brand_share_pct.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                    <td className="text-gray-600 dark:text-gray-400">
                      {row.top_3_models.map(m => `${m.model_name} (${m.count})`).join(', ')}
                    </td>
                    <td className="text-right">
                      <ChangeCountShared value={row.yoy_change} />
                    </td>
                    <td className="text-right">
                      <ChangeIndicatorShared value={row.yoy_change_pct} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {brandOrgTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-dark-border">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('analytics.pagination.pageOfOrgs', { page: brandOrgPage, pages: brandOrgTotalPages, total: brandOrgTotalItems.toLocaleString('nb-NO') })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchBrandOrgData(activeTab as BrandOrgTab, brandOrgPage - 1)}
                    disabled={brandOrgPage <= 1}
                    className="px-3 py-1 text-sm rounded-sm border disabled:opacity-50"
                  >
                    {t('analytics.pagination.prev')}
                  </button>
                  <button
                    onClick={() => fetchBrandOrgData(activeTab as BrandOrgTab, brandOrgPage + 1)}
                    disabled={brandOrgPage >= brandOrgTotalPages}
                    className="px-3 py-1 text-sm rounded-sm border disabled:opacity-50"
                  >
                    {t('analytics.pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
          </>
        ) : selectedBrands.length > 0 ? (
          <div className="card p-8 text-center text-gray-500">{t('analytics.empty.noDataBrandOrg')}</div>
        ) : (
          <div className="card p-8 text-center text-gray-500">{t('analytics.empty.pickBrand')}</div>
        )}
      </>
      )}
    </div>
  )
}
